import fs from 'fs/promises';
import path from 'path';
import 'dotenv/config';

/**
 * 视频保存工具
 * 负责从 URL 下载视频并保存到本地
 */

// 从环境变量读取视频输出目录，默认为项目的 outputs 目录
const VIDEO_OUTPUT_DIR =
  process.env.VIDEO_OUTPUT_DIR || path.join(process.cwd(), 'outputs');

/**
 * 视频保存错误类
 */
export class VideoSaverError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VideoSaverError';
  }
}

/**
 * 确保输出目录存在
 */
async function ensureOutputDir(): Promise<void> {
  try {
    await fs.access(VIDEO_OUTPUT_DIR);
  } catch {
    // 目录不存在，创建它
    console.error(`[VideoSaver] Creating output directory: ${VIDEO_OUTPUT_DIR}`);
    await fs.mkdir(VIDEO_OUTPUT_DIR, { recursive: true });
  }
}

/**
 * 生成带时间戳的视频文件名
 * @returns 文件名（不含路径）
 */
function generateVideoFilename(): string {
  const timestamp = new Date()
    .toISOString()
    .replace(/[:.]/g, '-')
    .replace('T', '_')
    .split('Z')[0];
  return `video_${timestamp}.mp4`;
}

/**
 * 从 URL 下载视频并保存到本地
 * @param videoUrl 视频 URL
 * @param customOutputDir 自定义输出目录（可选）
 * @returns 保存的视频文件绝对路径
 */
export async function downloadVideoFromUrl(
  videoUrl: string,
  customOutputDir?: string
): Promise<string> {
  console.error(`[VideoSaver] Downloading video from: ${videoUrl}`);

  const outputDir = customOutputDir || VIDEO_OUTPUT_DIR;

  // 确保输出目录存在
  try {
    await fs.access(outputDir);
  } catch {
    console.error(`[VideoSaver] Creating output directory: ${outputDir}`);
    await fs.mkdir(outputDir, { recursive: true });
  }

  // 生成文件名
  const filename = generateVideoFilename();
  const filepath = path.join(outputDir, filename);

  console.error(`[VideoSaver] Saving video to: ${filepath}`);

  try {
    // 使用 fetch 下载视频
    const response = await fetch(videoUrl);

    if (!response.ok) {
      throw new VideoSaverError(
        `Failed to download video: HTTP ${response.status} ${response.statusText}`
      );
    }

    // 检查 Content-Type
    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('video') && !contentType.includes('octet-stream')) {
      console.error(`[VideoSaver] Warning: Unexpected content type: ${contentType}`);
    }

    // 获取文件大小（如果可用）
    const contentLength = response.headers.get('content-length');
    if (contentLength) {
      const sizeMB = (parseInt(contentLength, 10) / (1024 * 1024)).toFixed(2);
      console.error(`[VideoSaver] Video size: ${sizeMB} MB`);
    }

    // 将响应转换为 ArrayBuffer
    const arrayBuffer = await response.arrayBuffer();
    const buffer = Buffer.from(arrayBuffer);

    // 写入文件
    await fs.writeFile(filepath, buffer);

    const fileSizeMB = (buffer.length / (1024 * 1024)).toFixed(2);
    console.error(`[VideoSaver] Video downloaded successfully! Size: ${fileSizeMB} MB`);
    console.error(`[VideoSaver] File saved at: ${filepath}`);

    // 返回绝对路径
    return path.resolve(filepath);
  } catch (error) {
    if (error instanceof VideoSaverError) {
      throw error;
    }

    if (error instanceof Error) {
      throw new VideoSaverError(`Failed to download or save video: ${error.message}`);
    }

    throw new VideoSaverError('Unknown error occurred while downloading video');
  }
}

/**
 * 读取本地图片文件并转换为 Base64
 * @param imagePath 图片文件路径
 * @returns Base64 编码的图片数据
 */
export async function readImageAsBase64(imagePath: string): Promise<string> {
  console.error(`[VideoSaver] Reading image from: ${imagePath}`);

  try {
    const imageBuffer = await fs.readFile(imagePath);
    const base64 = imageBuffer.toString('base64');

    // 尝试检测文件类型（简单方式）
    const ext = path.extname(imagePath).toLowerCase();
    let mimeType = 'image/jpeg'; // 默认

    if (ext === '.png') {
      mimeType = 'image/png';
    } else if (ext === '.jpg' || ext === '.jpeg') {
      mimeType = 'image/jpeg';
    } else if (ext === '.gif') {
      mimeType = 'image/gif';
    } else if (ext === '.webp') {
      mimeType = 'image/webp';
    }

    const dataUrl = `data:${mimeType};base64,${base64}`;
    console.error(`[VideoSaver] Image read successfully. Size: ${(imageBuffer.length / 1024).toFixed(2)} KB`);

    return dataUrl;
  } catch (error) {
    throw new VideoSaverError(`Failed to read image file: ${(error as Error).message}`);
  }
}
